/**
 * Visual Visioning API Client
 * 
 * Handles communication with the Visual Logic Auditor system
 * for remodel image generation and audit state management.
 */

import { getApiUrl } from './config';
import { getAuth } from 'firebase/auth';

/**
 * Check if current chat context is ready for visual visioning
 */
export async function auditVisualVisioning(chatMessages, currentMessage = '', currentImages = []) {
  const apiUrl = getApiUrl();
  const auth = getAuth();
  const token = await auth.currentUser?.getIdToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${apiUrl}/visual-visioning/audit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      chatHistory: chatMessages.map(msg => ({
        role: msg.role,
        content: msg.content || '',
        images: msg.images || [],
        timestamp: msg.timestamp
      })),
      currentMessage,
      currentImages
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || 'Vision audit failed');
  }

  return response.json();
}

/**
 * Generate remodel vision images based on chat context
 * @param {string|null} [conversationId] - Firestore landlord AI conversation id; links renders to this thread.
 */
export async function generateRemodelVision(
  chatMessages,
  currentMessage = '',
  currentImages = [],
  zipCode = null,
  conversationId = null
) {
  const apiUrl = getApiUrl();
  const auth = getAuth();
  const token = await auth.currentUser?.getIdToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${apiUrl}/visual-visioning/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      chatHistory: chatMessages.map(msg => ({
        role: msg.role,
        content: msg.content || '',
        images: msg.images || [],
        timestamp: msg.timestamp
      })),
      currentMessage,
      currentImages,
      zipCode,
      ...(conversationId ? { conversationId } : {}),
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || 'Vision generation failed');
  }

  return response.json();
}

/**
 * Fetch one landlord conversation (includes context.visualVisionGenerations after reload).
 */
export async function fetchLandlordConversation(conversationId) {
  const apiUrl = getApiUrl();
  const auth = getAuth();
  const token = await auth.currentUser?.getIdToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const uid = auth.currentUser?.uid;
  const url = `${apiUrl}/conversations/${encodeURIComponent(conversationId)}${uid ? `?userId=${encodeURIComponent(uid)}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Request failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Get results for a remodel vision generation session
 */
export async function getRemodelVisionResults(sessionId) {
  const apiUrl = getApiUrl();
  const auth = getAuth();
  const token = await auth.currentUser?.getIdToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${apiUrl}/visual-visioning/results/${sessionId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Request failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Mark a generated image as the primary visual reference for contractors
 */
export async function selectPrimaryVisualRef(imageId, threadId) {
  // This would typically save to the thread metadata
  // For now, just return success
  return { success: true, imageId, threadId };
}